import Phaser from 'phaser';
import { SCENES, DESIGN_WIDTH, DESIGN_HEIGHT, COLORS, COLORS_HEX, DEPTH } from '../constants';
import { SceneRouter } from '../systems/SceneRouter';
import { InputRouter } from '../systems/InputRouter';
import { AudioRouter } from '../systems/AudioRouter';
import { ProgressionSystem, ScoreBreakdown, StarRating } from '../systems/ProgressionSystem';
import { LevelLoader } from '../systems/LevelLoader';
import { LevelStatsTracker, TowerDamageStats } from '../systems/LevelStatsTracker';
import { GameState } from '../state/GameState';
import { ProgressState } from '../state/ProgressState';
import { UIButton } from '../ui/UIButton';
import { UIText } from '../ui/UIText';
import { UIPanel } from '../ui/UIPanel';
import type { ResultsPayload } from '../../shared/types';
import { formatTime, formatNumber } from '../../shared/utils';
import { fetchLevelScores } from '../systems/LevelScoresApi';

// ============================================
// ResultsScene (Step 12 Integration)
// ============================================

interface ResultsSceneData {
  results: ResultsPayload;
}

export class ResultsScene extends Phaser.Scene {
  private results!: ResultsPayload;
  private scoreBreakdown!: ScoreBreakdown;
  private starRating!: StarRating;
  private newUnlocks: string[] = [];
  private isNewHighScore: boolean = false;
  private isNewBestTime: boolean = false;
  private towerStats: TowerDamageStats[] = [];
  private showingStats: boolean = false;

  private yourBestText: Phaser.GameObjects.Text | null = null;
  private globalBestText: Phaser.GameObjects.Text | null = null;

  private buttons: UIButton[] = [];
  private selectedIndex: number = 0;

  constructor() {
    super({ key: SCENES.RESULTS });
  }

  init(data: ResultsSceneData): void {
    this.results = data.results || this.getDefaultResults();
  }

  private getDefaultResults(): ResultsPayload {
    return {
      scenarioId: 'F1',
      victory: false,
      wavesCompleted: 0,
      totalWaves: 5,
      monstersKilled: 0,
      monsterDamageDealt: 0,
      towersBuilt: 0,
      villagersLost: 0,
      villagersRemaining: 0,
      maxVillagers: 20,
      coinsRemaining: 0,
      gemsRemaining: 0,
      timeElapsed: 0,
      score: 0,
      waveSkipBonuses: 0,
      playerCount: 1,
    };
  }

  create(): void {
    console.log('[ResultsScene] Showing results...');
    SceneRouter.setCurrentScene(SCENES.RESULTS);
    InputRouter.init(this);
    InputRouter.setMenuMode(true); // Higher deadzone for menu navigation

    AudioRouter.startMenuMusic(this);

    // Process results through ProgressionSystem
    const processed = ProgressionSystem.processCompletion(this.results);
    this.scoreBreakdown = processed.scoreBreakdown;
    this.starRating = processed.starRating;
    this.newUnlocks = processed.newUnlocks;
    this.isNewHighScore = processed.isNewHighScore;
    this.isNewBestTime = processed.isNewBestTime;

    // Get and save level stats
    const levelStats = LevelStatsTracker.finishLevel(this.results.timeElapsed);
    if (levelStats) {
      this.towerStats = LevelStatsTracker.getSortedTowerStats();
      ProgressState.saveLevelStats(levelStats);
    }

    // Update results with calculated score
    this.results.score = this.scoreBreakdown.totalScore;

    // Background panel - sized to fit score breakdown, tower stats, unlocks, and buttons
    new UIPanel(this, {
      x: DESIGN_WIDTH / 2,
      y: DESIGN_HEIGHT / 2,
      width: 720,
      height: 820,
    });

    // Victory/Defeat title
    const titleColor = this.results.victory ? COLORS.SECONDARY : COLORS.DANGER;
    const titleText = this.results.victory ? 'VICTORY!' : 'DEFEAT';

    new UIText(this, {
      x: DESIGN_WIDTH / 2,
      y: 90,
      text: titleText,
      fontSize: 48,
      color: titleColor,
      fontStyle: 'bold',
    });

    // Star rating (only for victory)
    if (this.results.victory) {
      this.createStarRating();
    }

    // Score
    new UIText(this, {
      x: DESIGN_WIDTH / 2,
      y: 180,
      text: `Score: ${formatNumber(this.scoreBreakdown.totalScore)}`,
      fontSize: 32,
      color: COLORS.ACCENT,
    });

    // New high score indicator
    if (this.isNewHighScore && this.results.victory) {
      const newHs = this.add.text(DESIGN_WIDTH / 2, 210, '★ NEW HIGH SCORE! ★', {
        fontSize: '16px',
        color: COLORS_HEX.TIKI_GOLD,
        fontStyle: 'bold',
      });
      newHs.setOrigin(0.5);

      // Pulsing animation
      this.tweens.add({
        targets: newHs,
        scale: 1.1,
        duration: 500,
        yoyo: true,
        repeat: -1,
      });
    }

    // Score breakdown
    this.createScoreBreakdown();

    // Tower damage stats table
    this.createTowerStatsTable();

    // New unlocks notification
    if (this.newUnlocks.length > 0) {
      this.createUnlockNotification();
    }

    // Buttons
    this.createButtons();

    this.updateSelection();
  }

  private createStarRating(): void {
    const starY = 140;
    const starSpacing = 40;
    const startX = DESIGN_WIDTH / 2 - starSpacing;

    for (let i = 0; i < 3; i++) {
      const filled = i < this.starRating.stars;
      const star = this.add.text(startX + i * starSpacing, starY, filled ? '★' : '☆', {
        fontSize: '36px',
        color: filled ? COLORS_HEX.TIKI_GOLD : COLORS_HEX.DEEP_AMBER,
      });
      star.setOrigin(0.5);

      // Animate stars appearing
      if (filled) {
        star.setScale(0);
        this.tweens.add({
          targets: star,
          scale: 1,
          duration: 300,
          delay: i * 200,
          ease: 'Back.easeOut',
        });
      }
    }

    // Villager survival display - use actual numbers from results
    const saved = this.results.villagersRemaining;
    const max = this.results.maxVillagers || 20;
    const percent = Math.round((saved / max) * 100);
    this.add.text(DESIGN_WIDTH / 2, starY + 30, `${saved}/${max} villagers saved (${percent}%)`, {
      fontSize: '14px',
      color: COLORS_HEX.TEXT_SECONDARY,
    }).setOrigin(0.5);
  }

  private createScoreBreakdown(): void {
    const startY = 250;
    const lineHeight = 28;
    const leftX = DESIGN_WIDTH / 2 - 150;
    const rightX = DESIGN_WIDTH / 2 + 150;

    const breakdown = this.scoreBreakdown;

    const lines: Array<{ label: string; value: string; positive: boolean }> = [
      { label: `Waves Completed (${breakdown.wavesCompleted}):`, value: `+${breakdown.wavePoints}`, positive: true },
      { label: `Villagers Saved (${breakdown.villagersRemaining}):`, value: `+${breakdown.villagerPoints}`, positive: true },
      { label: `Monster Damage (${formatNumber(breakdown.monsterDamageDealt)} HP):`, value: `+${breakdown.damagePoints}`, positive: true },
      { label: `Gems Remaining (${breakdown.gemsRemaining}):`, value: `+${breakdown.gemPoints}`, positive: true },
      { label: `Coins Remaining (${breakdown.coinsRemaining}):`, value: `+${breakdown.coinPoints}`, positive: true },
      { label: `Towers Built (${breakdown.towersBuilt}):`, value: `-${breakdown.towerPenalty}`, positive: false },
    ];
    
    // Only show skip bonus if there were any
    if (breakdown.waveSkipBonuses > 0) {
      lines.push({ label: `Wave Skips (${breakdown.waveSkipBonuses}):`, value: `+${breakdown.skipBonusPoints}`, positive: true });
    }
    
    lines.push({ label: this.results.victory ? 'Victory Bonus:' : 'Defeat Penalty:', value: breakdown.victoryBonus >= 0 ? `+${breakdown.victoryBonus}` : `${breakdown.victoryBonus}`, positive: breakdown.victoryBonus >= 0 });

    lines.forEach((line, i) => {
      const y = startY + i * lineHeight;

      // Label
      const label = this.add.text(leftX, y, line.label, {
        fontSize: '16px',
        color: COLORS_HEX.NEON_ICE,
      });
      label.setOrigin(0, 0.5);
      label.setDepth(DEPTH.UI_TEXT);

      // Value
      const value = this.add.text(rightX, y, line.value, {
        fontSize: '16px',
        color: line.positive ? COLORS_HEX.GLITCH_CYAN : COLORS_HEX.DANGER,
        fontStyle: 'bold',
      });
      value.setOrigin(1, 0.5);
      value.setDepth(DEPTH.UI_TEXT);
    });

    // Divider
    const graphics = this.add.graphics();
    graphics.lineStyle(1, COLORS.DEEP_AMBER, 1);
    graphics.lineBetween(leftX, startY + lines.length * lineHeight + 5, rightX, startY + lines.length * lineHeight + 5);
    graphics.setDepth(DEPTH.UI_TEXT);

    // Total
    const totalY = startY + lines.length * lineHeight + 25;
    const totalLabel = this.add.text(leftX, totalY, 'TOTAL SCORE:', {
      fontSize: '18px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    totalLabel.setOrigin(0, 0.5);
    totalLabel.setDepth(DEPTH.UI_TEXT);

    const totalValue = this.add.text(rightX, totalY, formatNumber(breakdown.totalScore), {
      fontSize: '18px',
      color: COLORS_HEX.GLITCH_CYAN,
      fontStyle: 'bold',
    });
    totalValue.setOrigin(1, 0.5);
    totalValue.setDepth(DEPTH.UI_TEXT);

    // Time
    const timeY = totalY + 35;
    const timeText = this.add.text(DESIGN_WIDTH / 2, timeY, `Time: ${formatTime(this.results.timeElapsed)}`, {
      fontSize: '14px',
      color: COLORS_HEX.TEXT_SECONDARY,
    });
    timeText.setOrigin(0.5);
    timeText.setDepth(DEPTH.UI_TEXT);

    if (this.isNewBestTime && this.results.victory) {
      const bestTime = this.add.text(DESIGN_WIDTH / 2, timeY + 18, '(New Best Time!)', {
        fontSize: '12px',
        color: COLORS_HEX.GLITCH_CYAN,
      });
      bestTime.setOrigin(0.5);
      bestTime.setDepth(DEPTH.UI_TEXT);
    }

    // Your all-time best (from ProgressState)
    const progress = ProgressState.getScenarioProgress(this.results.scenarioId);
    const yourBestY = timeY + 50;
    const pLabel = (progress.bestPlayerCount ?? 1) > 1 ? ` · ${progress.bestPlayerCount}P` : '';
    this.yourBestText = this.add.text(DESIGN_WIDTH / 2, yourBestY, `Your best: ${formatNumber(progress.bestScore)} (${formatTime(progress.bestTime)})${pLabel}`, {
      fontSize: '14px',
      color: COLORS_HEX.TEXT_SECONDARY,
    });
    this.yourBestText.setOrigin(0.5);
    this.yourBestText.setDepth(DEPTH.UI_TEXT);

    // Global best — load async, update when ready (no delay; works offline if fetch fails)
    this.globalBestText = this.add.text(DESIGN_WIDTH / 2, yourBestY + 22, 'Global best: —', {
      fontSize: '14px',
      color: COLORS_HEX.TEXT_SECONDARY,
    });
    this.globalBestText.setOrigin(0.5);
    this.globalBestText.setDepth(DEPTH.UI_TEXT);
    fetchLevelScores(this.results.scenarioId, 1).then((data) => {
      if (!this.globalBestText || !this.scene.isActive()) return;
      if (data?.scores?.length) {
        const top = data.scores[0];
        const topPLabel = (top.player_count ?? 1) > 1 ? ` · ${top.player_count}P` : '';
        this.globalBestText!.setText(`Global best: ${formatNumber(top.score)} (${formatTime(top.best_time_seconds)})${topPLabel} by ${top.display_name}`);
      }
    }).catch(() => {});
  }

  private createTowerStatsTable(): void {
    if (this.towerStats.length === 0) return;

    const startY = 490;
    const tableX = DESIGN_WIDTH / 2;

    // Header
    const header = this.add.text(tableX, startY, 'TOWER DAMAGE STATS', {
      fontSize: '16px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    header.setOrigin(0.5);
    header.setDepth(DEPTH.UI_TEXT);

    // Column headers
    const colY = startY + 25;
    const cols = [
      { x: tableX - 180, text: 'Tower', align: 0 },
      { x: tableX - 30, text: 'Damage', align: 1 },
      { x: tableX + 60, text: 'Kills', align: 1 },
      { x: tableX + 130, text: 'Cost', align: 1 },
      { x: tableX + 200, text: 'Dmg/g', align: 1 },
    ];

    cols.forEach(col => {
      const colHeader = this.add.text(col.x, colY, col.text, {
        fontSize: '11px',
        color: '#aaaaaa',
      });
      colHeader.setOrigin(col.align, 0.5);
      colHeader.setDepth(DEPTH.UI_TEXT);
    });

    // Table rows - show top 6 towers
    const rowHeight = 20;
    const maxRows = 6;
    const visibleStats = this.towerStats.slice(0, maxRows);

    visibleStats.forEach((stat, index) => {
      const rowY = colY + 20 + index * rowHeight;
      const rowColor = index % 2 === 0 ? '#cccccc' : '#aaaaaa';

      // Tower name
      const name = this.add.text(cols[0].x, rowY, stat.displayName, {
        fontSize: '12px',
        color: rowColor,
      });
      name.setOrigin(0, 0.5);
      name.setDepth(DEPTH.UI_TEXT);

      // Damage
      const damage = this.add.text(cols[1].x, rowY, this.formatDamage(stat.totalDamage), {
        fontSize: '12px',
        color: stat.totalDamage > 0 ? '#ffcc00' : '#666666',
      });
      damage.setOrigin(1, 0.5);
      damage.setDepth(DEPTH.UI_TEXT);

      // Kills
      const kills = this.add.text(cols[2].x, rowY, stat.kills.toString(), {
        fontSize: '12px',
        color: stat.kills > 0 ? '#00ff00' : '#666666',
      });
      kills.setOrigin(1, 0.5);
      kills.setDepth(DEPTH.UI_TEXT);

      // Cost
      const cost = this.add.text(cols[3].x, rowY, stat.totalCost > 0 ? `${stat.totalCost}g` : '-', {
        fontSize: '12px',
        color: '#aaaaaa',
      });
      cost.setOrigin(1, 0.5);
      cost.setDepth(DEPTH.UI_TEXT);

      // Damage per gold
      const dpg = this.add.text(cols[4].x, rowY, stat.damagePerGold > 0 ? stat.damagePerGold.toFixed(2) : '-', {
        fontSize: '12px',
        color: stat.damagePerGold > 1 ? '#00ff00' : '#aaaaaa',
      });
      dpg.setOrigin(1, 0.5);
      dpg.setDepth(DEPTH.UI_TEXT);
    });
  }

  private formatDamage(damage: number): string {
    if (damage >= 1000000) {
      return `${(damage / 1000000).toFixed(1)}M`;
    } else if (damage >= 1000) {
      return `${(damage / 1000).toFixed(1)}K`;
    }
    return damage.toString();
  }

  private createUnlockNotification(): void {
    const y = 650;

    // Get names of unlocked scenarios
    const names = this.newUnlocks.map(id => {
      const def = LevelLoader.getScenarioDef(id);
      return def?.name ?? id;
    });

    const text = `🔓 UNLOCKED: ${names.join(', ')}`;

    const notification = this.add.text(DESIGN_WIDTH / 2, y, text, {
      fontSize: '18px',
      color: '#00ff00',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    });
    notification.setOrigin(0.5);
    notification.setDepth(DEPTH.UI_TEXT);

    // Flash animation
    this.tweens.add({
      targets: notification,
      alpha: 0.5,
      duration: 500,
      yoyo: true,
      repeat: 2,
    });
  }

  private createButtons(): void {
    const buttonsY = 700;
    this.buttons = [];

    if (this.results.victory) {
      // Check if there's a next level
      const levelNum = parseInt(this.results.scenarioId.replace('level-', ''));
      const hasNextLevel = !isNaN(levelNum) && levelNum < 5;

      if (hasNextLevel) {
        this.buttons.push(
          new UIButton(this, {
            x: DESIGN_WIDTH / 2 - 110,
            y: buttonsY,
            text: 'Next Level',
            onClick: () => this.goToNextLevel(),
          })
        );

        this.buttons.push(
          new UIButton(this, {
            x: DESIGN_WIDTH / 2 + 110,
            y: buttonsY,
            text: 'Map',
            onClick: () => this.goToMap(),
          })
        );
      } else {
        // Final level completed
        this.buttons.push(
          new UIButton(this, {
            x: DESIGN_WIDTH / 2,
            y: buttonsY,
            text: 'Map',
            onClick: () => this.goToMap(),
          })
        );
      }
    } else {
      // Defeat - show Retry and Map
      this.buttons.push(
        new UIButton(this, {
          x: DESIGN_WIDTH / 2 - 110,
          y: buttonsY,
          text: 'Retry',
          onClick: () => this.retry(),
        })
      );

      this.buttons.push(
        new UIButton(this, {
          x: DESIGN_WIDTH / 2 + 110,
          y: buttonsY,
          text: 'Map',
          onClick: () => this.goToMap(),
        })
      );
    }

    this.selectedIndex = 0;
  }

  private updateSelection(): void {
    this.buttons.forEach((btn, i) => {
      btn.setSelected(i === this.selectedIndex);
    });
  }

  private navigateLeft(): void {
    this.selectedIndex = Math.max(0, this.selectedIndex - 1);
    this.updateSelection();
  }

  private navigateRight(): void {
    this.selectedIndex = Math.min(this.buttons.length - 1, this.selectedIndex + 1);
    this.updateSelection();
  }

  private confirmSelection(): void {
    this.buttons[this.selectedIndex].click();
  }

  private goToNextLevel(): void {
    const levelNum = parseInt(this.results.scenarioId.replace('level-', ''));
    const nextLevelId = `level-${levelNum + 1}`;
    GameState.setSelectedScenario(nextLevelId);
    SceneRouter.goToGameplay(SCENES.RESULTS, nextLevelId);
  }

  private retry(): void {
    SceneRouter.restartGameplay(SCENES.RESULTS, this.results.scenarioId);
  }

  private goToMap(): void {
    SceneRouter.goToMap(SCENES.RESULTS);
  }

  update(): void {
    InputRouter.update();

    // Handle navigation
    if (InputRouter.anyPlayerJustPressed('MOVE_LEFT')) {
      this.navigateLeft();
    }
    if (InputRouter.anyPlayerJustPressed('MOVE_RIGHT')) {
      this.navigateRight();
    }
    if (InputRouter.anyPlayerJustPressed('INTERACT') || InputRouter.anyPlayerJustPressed('START')) {
      this.confirmSelection();
    }

    // Must call lateUpdate at end of frame to properly track "just pressed" state
    InputRouter.lateUpdate();
  }
}
